import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  IdentificationBadge, CircleNotch, UserPlus, SealCheck, SealWarning, Plus,
  Crown, Users, Presentation, PencilSimple,
} from "@phosphor-icons/react";

export default function ClubBoard() {
  const [clubs, setClubs] = useState([]);
  const [activeClub, setActiveClub] = useState(null);
  const [board, setBoard] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presentation, setPresentation] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRole, setAssignRole] = useState("");
  const [assignMember, setAssignMember] = useState("");
  const [vacateFor, setVacateFor] = useState(null);
  const [vacateReason, setVacateReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/directory/clubs");
        setClubs(r.data.clubs);
        if (r.data.clubs.length) setActiveClub(r.data.clubs[0].club_id);
      } catch (_) {}
    })();
  }, []);

  const load = async (club) => {
    setLoading(true);
    try {
      const [b, m] = await Promise.all([
        api.get(`/clubs/${club}/appointments`),
        api.get(`/directory/clubs/${club}/members`),
      ]);
      setBoard(b.data);
      setMembers(m.data.members);
    } catch (_) { setBoard(null); }
    setLoading(false);
  };

  useEffect(() => { if (activeClub) load(activeClub); }, [activeClub]);

  const doAssign = async () => {
    if (!assignRole || !assignMember) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${activeClub}/appointments`, { role_id: assignRole, member_id: assignMember });
      toast.success("Appointment saved");
      setAssignOpen(false); setAssignRole(""); setAssignMember("");
      await load(activeClub);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
    setBusy(false);
  };

  const doVacate = async () => {
    setBusy(true);
    try {
      await api.post(`/clubs/${activeClub}/appointments/${vacateFor.assignment_id}/vacate`, { vacancy_reason: vacateReason || "unspecified" });
      toast.success("Position vacated (history preserved)");
      setVacateFor(null); setVacateReason("");
      await load(activeClub);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not vacate"); }
    setBusy(false);
  };

  // Group the catalogue into named committees (role_group), preserving group/display order.
  const committees = useMemo(() => {
    if (!board) return [];
    const holdersFor = (roleId) => (board.assignments || []).filter((a) => a.role_id === roleId && !a.vacant);
    const vacancyFor = (roleId) => (board.assignments || []).find((a) => a.role_id === roleId && a.vacant);
    const groups = {};
    board.catalogue.forEach((role) => {
      const g = role.role_group || "Other";
      (groups[g] = groups[g] || []).push({
        ...role,
        holders: holdersFor(role.role_id),
        vacancy: vacancyFor(role.role_id),
      });
    });
    return Object.entries(groups).map(([name, roles]) => ({
      name,
      chair: roles.find((r) => r.is_chair) || null,
      roles,
    }));
  }, [board]);

  const canManage = board?.can_manage && !presentation;

  const RoleBlock = ({ role, isChair }) => (
    <div data-testid={`role-block-${role.role_id}`} className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-medium">
          {isChair && <Crown size={15} weight="fill" className="text-accent" />}
          {role.role_name}
          {!presentation && (
            <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 border border-black/10 dark:border-white/10 text-muted-foreground">
              {role.holder_policy === "singular" ? "single" : "multiple"}
            </span>
          )}
        </span>
      </div>
      <div className="mt-1.5 flex flex-col gap-1.5 pl-6">
        {role.holders.length === 0 && (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <SealWarning size={15} weight="duotone" className="text-accent" />
            {role.vacancy ? `Vacant — ${role.vacancy.vacancy_reason || "unspecified"}` : "Unfilled"}
          </span>
        )}
        {role.holders.map((h) => (
          <div key={h.assignment_id} data-testid={`holder-${h.assignment_id}`} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm flex-wrap">
              <SealCheck size={15} weight="fill" className="text-primary" /> {h.member_name}
              {h.holder_inactive && (
                <span data-testid={`holder-inactive-${h.assignment_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A1C1C]">
                  <SealWarning size={13} weight="fill" /> no longer active — replace
                </span>
              )}
            </span>
            {canManage && (
              <button data-testid={`vacate-${h.assignment_id}`} onClick={() => setVacateFor(h)}
                className="text-[11px] uppercase tracking-[0.12em] text-destructive hover:underline">Vacate</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl" data-testid="board-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
        <IdentificationBadge size={14} weight="fill" className="text-primary" /> Board &amp; Committees
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl leading-[0.95]">Appointments</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {board?.club_id && clubs.find((c) => c.club_id === board.club_id)?.club_name} · Rotary Year {board?.rotary_year_id?.replace("ry-", "") || "2026-27"}
          </p>
        </div>
        <button data-testid="toggle-presentation" onClick={() => setPresentation((p) => !p)}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.12em] border border-black/10 dark:border-white/10 hover:bg-secondary transition-colors active:scale-95">
          {presentation ? <><PencilSimple size={15} weight="bold" /> Manage view</> : <><Presentation size={15} weight="bold" /> Presentation</>}
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="board-club-tabs">
        {clubs.map((c) => (
          <button key={c.club_id} data-testid={`board-club-${c.club_id}`} onClick={() => setActiveClub(c.club_id)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.12em] border transition-colors active:scale-95 ${
              activeClub === c.club_id ? "border-primary bg-primary text-white" : "border-black/10 dark:border-white/10 hover:bg-secondary"}`}>
            {c.club_name}
          </button>
        ))}
      </div>

      {canManage && (
        <button data-testid="open-assign-button" onClick={() => setAssignOpen(true)}
          className="mt-6 inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-primary/90 transition-colors active:scale-95">
          <UserPlus size={16} weight="bold" /> Assign a role
        </button>
      )}

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground text-sm">
          <CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading board…
        </div>
      ) : (
        <div className={`mt-6 ${presentation ? "border border-black/10 dark:border-white/10 bg-surface p-6 sm:p-8" : "grid md:grid-cols-2 gap-4"}`} data-testid="appointment-sheet">
          {presentation && (
            <div className="mb-6 text-center">
              <p className="font-dossier font-bold text-2xl">
                {clubs.find((c) => c.club_id === board?.club_id)?.club_name}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">Board &amp; Committees · RY 2026-27</p>
            </div>
          )}
          {committees.map((cm) => (
            <div key={cm.name} data-testid={`committee-${cm.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={presentation ? "mb-6 last:mb-0" : "border border-black/10 dark:border-white/10 bg-surface"}>
              <div className={`flex items-center gap-2 ${presentation ? "border-b border-black/10 dark:border-white/10 pb-2 mb-1" : "px-5 py-3 border-b border-black/10 dark:border-white/10"}`}>
                <Users size={16} weight="duotone" className="text-primary" />
                <span className="text-[13px] uppercase tracking-[0.18em] font-semibold">{cm.name}</span>
                {cm.chair && (
                  <span className="ml-auto flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    <Crown size={12} weight="fill" className="text-accent" /> Chair: {cm.chair.holders?.[0]?.member_name || "—"}
                  </span>
                )}
              </div>
              <div className={`${presentation ? "" : "px-5 py-2"} divide-y divide-black/5 dark:divide-white/5`}>
                {cm.roles.map((role) => <RoleBlock key={role.role_id} role={role} isChair={role.is_chair} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Assign a role</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Role</span>
              <select data-testid="assign-role-select" value={assignRole} onChange={(e) => setAssignRole(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 text-sm bg-surface border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none">
                <option value="">Select a role…</option>
                {board?.catalogue.map((c) => (
                  <option key={c.role_id} value={c.role_id}>{c.role_group} · {c.role_name} ({c.holder_policy})</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Member</span>
              <select data-testid="assign-member-select" value={assignMember} onChange={(e) => setAssignMember(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 text-sm bg-surface border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none">
                <option value="">Select a member…</option>
                {members.map((m) => <option key={m.member_id} value={m.member_id}>{m.full_name}</option>)}
              </select>
            </label>
          </div>
          <DialogFooter>
            <button data-testid="confirm-assign-button" onClick={doAssign} disabled={busy || !assignRole || !assignMember}
              className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-primary/90 transition-colors active:scale-95 disabled:opacity-60">
              {busy ? <CircleNotch size={15} className="animate-spin" weight="bold" /> : <Plus size={15} weight="bold" />} Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vacate dialog */}
      <Dialog open={!!vacateFor} onOpenChange={(o) => !o && setVacateFor(null)}>
        <DialogContent data-testid="vacate-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Vacate position</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Closing <span className="text-foreground font-medium">{vacateFor?.role_name}</span> held by {vacateFor?.member_name}.
            The prior record is kept in history; a vacancy is recorded.
          </p>
          <input data-testid="vacate-reason-input" value={vacateReason} onChange={(e) => setVacateReason(e.target.value)}
            placeholder="Reason (e.g. resigned, relocated)"
            className="mt-2 w-full px-3 py-2.5 text-sm bg-surface border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none" />
          <DialogFooter>
            <button data-testid="confirm-vacate-button" onClick={doVacate} disabled={busy}
              className="inline-flex items-center gap-2 bg-destructive text-destructive-foreground px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:opacity-90 transition-opacity active:scale-95 disabled:opacity-60">
              {busy ? <CircleNotch size={15} className="animate-spin" weight="bold" /> : <SealWarning size={15} weight="bold" />} Vacate
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
